import { Module } from "vuex";
import store from ".";
import { Button, ModalOptions } from "@/models/modal";
import { FormChange } from "@/models/form";

const modalModule: Module<any, any> = {
  namespaced: true,
  state: {
    active: false,
    addressModalActive: false as boolean,
    cancellationModalActive: false as boolean,
    cancellationReferenceId: null as string | null,
    currentAddressModalActive: false as boolean,
    servicerPayoffAddressModalActive: false as boolean,
    selfCertificationDetailsModalActive: false as boolean,
    confirmManualIncomeVerificationModalActive: false as boolean,
    measureOneModalActive: false as boolean,
    title: "",
    textAlign: "center",
    description: "",
    buttons: [] as Button[],
    centerButtons: false as boolean,
    fullScreen: false,
    allowBackgroundScroll: true,
    countDownId: 0 as number,
    type: "",
    loading: false,
    changes: [] as FormChange[],
    buttonsClass: "",
  },
  mutations: {
    closeAllModals(state: any) {
      state.active = false;
      state.addressModalActive = false;
      state.cancellationModalActive = false;
      state.cancellationReferenceId = null;
      state.currentAddressModalActive = false;
      state.servicerPayoffAddressModalActive = false;
      state.selfCertificationDetailsModalActive = false;
      state.confirmManualIncomeVerificationModalActive = false;
      state.measureOneModalActive = false;
    },
    openModal(state: any, options: ModalOptions) {
      // close open modals
      state.active = false;

      // open this modal
      state.active = true;
      state.loading = options?.loading || false;
      state.title = options?.title || options;
      state.description = options?.description || "";
      state.html = options?.html || "";
      state.buttons = options?.buttons || [];
      state.fullscreen = options?.fullscreen || false;
      state.centerButtons = options.centerButtons || false;
      state.allowBackgroundScroll = options.allowBackgroundScroll || false;
      state.changes = options?.changes || null;
      state.textAlign = options?.textAlign || "center";
      state.buttonsPosition = options?.buttonsPosition || "";
      state.titleClassList = options?.titleClassList || [];
      state.type = options?.type || "";
    },
    closeModal(state: any) {
      state.active = false;
      if (state.countDownId > 0) {
        clearInterval(state.countDownId);
      }
    },
    openAddressModal(state: any) {
      state.addressModalActive = true;
    },
    closeAddressModal(state: any) {
      state.addressModalActive = false;
    },
    openCurrentAddressModal(state: any) {
      state.currentAddressModalActive = true;
    },
    closeCurrentAddressModal(state: any) {
      state.currentAddressModalActive = false;
    },
    openCancellationModal(state: any, referenceId?: string) {
      state.cancellationModalActive = true;
      state.cancellationReferenceId = referenceId ?? null;
    },
    closeCancellationModal(state: any) {
      state.cancellationModalActive = false;
      state.cancellationReferenceId = null;
    },
    openServicerPayoffAddressModal(state: any) {
      state.servicerPayoffAddressModalActive = true;
    },
    closeServicerPayoffAddressModal(state: any) {
      state.servicerPayoffAddressModalActive = false;
    },
    openSelfCertificationDetailsModal(state: any) {
      state.selfCertificationDetailsModalActive = true;
    },
    closeSelfCertificationDetailsModal(state: any) {
      state.selfCertificationDetailsModalActive = false;
    },
    setCountDownModal(state: any, countDown: number) {
      state.countDownId = countDown;
    },
    setBodyModal(state: any, body: string) {
      state.html = body;
    },
    openConfirmManualIncomeVerificationModal(state: any) {
      state.confirmManualIncomeVerificationModalActive = true;
    },
    closeConfirmManualIncomeVerificationModal(state: any) {
      state.confirmManualIncomeVerificationModalActive = false;
    },
    openMeasureOneModal(state: any) {
      state.measureOneModalActive = true;
    },
    closeMeasureOneModal(state: any) {
      state.measureOneModalActive = false;
    },
    setModalLoading(state: any, flag: boolean) {
      state.modal.loading = flag || true;
    },
    setButtonLoading(state: any, payload: { index: number; flag: boolean }) {
      state.buttons[payload.index].loading = payload.flag;
    },
  },
  actions: {
    openModal({ commit }, payload) {
      commit("openModal", payload);
    },
    closeModal({ commit }, payload) {
      commit("closeModal", payload);
    },
    setCountDownModal({ commit }, payload) {
      commit("setCountDownModal", payload);
    },
    setBodyModal({ commit }, payload) {
      commit("setBodyModal", payload);
    },
    openAddressModal({ commit }) {
      commit("openAddressModal");
    },
    closeAddressModal({ commit }) {
      commit("closeAddressModal");
      store.commit("forms/resetAddressForm");
    },
    openCurrentAddressModal({ commit }) {
      commit("openCurrentAddressModal");
    },
    closeCurrentAddressModal({ commit }) {
      commit("closeCurrentAddressModal");
      store.commit("forms/resetCurrentAddressForm");
    },
    openCancellationModal({ commit }, referenceId?: string) {
      commit("openCancellationModal", referenceId);
    },
    closeCancellationModal({ commit }) {
      commit("closeCancellationModal");
    },
    openServicerPayoffAddressModal({ commit }) {
      commit("openServicerPayoffAddressModal");
    },
    closeServicerPayoffAddressModal({ commit }) {
      commit("closeServicerPayoffAddressModal");
      store.commit("forms/resetServicerPayoffAddressForm");
    },
    openSelfCertificationDetailsModal({ commit }) {
      commit("openSelfCertificationDetailsModal");
    },
    closeSelfCertificationDetailsModal({ commit }) {
      commit("closeSelfCertificationDetailsModal");
    },
    openConfirmManualIncomeVerificationModal({ commit }) {
      commit("openConfirmManualIncomeVerificationModal");
    },
    closeConfirmManualIncomeVerificationModal({ commit }) {
      commit("closeConfirmManualIncomeVerificationModal");
    },
    openMeasureOneModal({ commit }) {
      commit("openMeasureOneModal");
    },
    closeMeasureOneModal({ commit }) {
      commit("closeMeasureOneModal");
    },
    setModalLoading({ commit }, flag: boolean) {
      commit("setModalLoading", flag);
    },
    setButtonLoading({ commit }, payload: { index: number; flag: boolean }) {
      commit("setButtonLoading", payload);
    },
  },
  getters: {
    currentAddressModalActive(state: any) {
      return state.addressModalActive;
    },
    addressModalActive(state: any) {
      return state.addressModalActive;
    },
    cancellationModalActive(state: any) {
      return state.cancellationModalActive;
    },
    cancellationReferenceId(state: any) {
      return state.cancellationReferenceId;
    },
    servicerPayoffAddressModalActive(state: any) {
      return state.servicerPayoffAddressModalActive;
    },
    selfCertificationDetailsModalActive(state: any) {
      return state.selfCertificationDetailsModalActive;
    },
    confirmManualIncomeVerificationModalActive(state: any) {
      return state.confirmManualIncomeVerificationModalActive;
    },
    measureOneModalActive(state: any) {
      return state.measureOneModalActive;
    },
  },
};
export default modalModule;
